import React, { Children, useMemo } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import capitalize from 'lodash/capitalize'

import { AccordionDetails, Box, Divider, Typography, Tab, Tabs } from '@mui/material'
import Grid from '@mui/material/Grid'
import { grey } from '@mui/material/colors'

import { get } from 'utils/lodash'
import { INVOICE_STATUS } from 'settings/constants/billing'
import { PAYMENT_STATUS } from 'settings/constants/payment'

import T from 'T'
import InvoiceActivity from 'components/customer-details/content/common/InvoiceActivity'
import PaymentHistoryActivity from 'components/customer-details/content/common/PaymentHistoryActivity'
import HHSectionPlaceholder from '../../common/HHSectionPlaceholder'
import TabPanel from '../../common/TabPanel'
import EmailActivity from '../../customer-details/content/common/EmailActivity'
import { PAYMENT_ACTIVITY_TABS_ID } from '../../customer-details/content/payments/settings'
import ActivityHeader from '../../customer-details/content/common/ActivityHeader'
import usePaymentProvider from 'components/customer-details/content/payment-details/usePaymentProvider'

const { APPROVED, REVERSED, VOIDED, REFUNDED, DECLINED } = PAYMENT_STATUS
const { PAID } = INVOICE_STATUS
const { INVOICE_TAB_ID, EMAIL_TAB_ID, HISTORY_TAB_ID } = PAYMENT_ACTIVITY_TABS_ID

const PaymentActivityFeed = ({
  accountId = undefined,
  invoices = [],
  accountCredit = {},
  transactionStatus = '',
  emailEvents = [],
  paymentHistoryEvents = [],
  selectedTab,
  onSelectedTabChange,
  Component = AccordionDetails,
}) => {
  const { isFortis } = usePaymentProvider(accountId)
  const isApproved = transactionStatus === APPROVED

  // Compute running totals for history tab: reversed events subtract, declined events are ignored, others add
  const historyWithTotals = useMemo(() => {
    if (!Array.isArray(paymentHistoryEvents) || paymentHistoryEvents.length === 0) return []
    const chronological = [...paymentHistoryEvents].reverse()
    let runningTotal = 0
    const withTotals = chronological.map(event => {
      const amount = event.transactionAmountCents || 0
      const status = capitalize(event.transactionStatus)
      if (status === DECLINED) {
        return { ...event, runningTotalCents: runningTotal }
      }
      runningTotal += (status === REVERSED || status === VOIDED || status === REFUNDED) ? -amount : amount
      return { ...event, runningTotalCents: runningTotal }
    })
    return withTotals.reverse()
  }, [paymentHistoryEvents])

  const allInvoices = isEmpty(accountCredit)
    ? invoices
    : [
        ...invoices,
        {
          isAccountCredit: true,
          status: get(accountCredit, 'amountCents', 0) >= 0 ? PAID : 'Debit',
          totalCents: get(accountCredit, 'amountCents', 0),
          paymentAppliedAmountCents: get(accountCredit, 'amountCents', 0),
          title: capitalize(get(accountCredit, 'amountCents', 0) >= 0 ? T.ACCOUNT_CREDIT : T.ACCOUNT_DEBIT),
        },
      ]

  return (
    <Component
      sx={{
        px: 2,
        pt: 1,
        pb: 0,
        backgroundColor: grey[50],
        borderBottom: '1px solid',
        borderTop: '1px solid',
        borderColor: 'border.light',
      }}
    >
      <Box width="100%" pl={4.5}>
        <Typography variant="h6" fontWeight={500} my={1.5}>
          {T.ACTIVITY}
        </Typography>

        <Tabs value={selectedTab} onChange={onSelectedTabChange} sx={{ mb: 2, button: { textTransform: 'capitalize' } }}>
          <Tab value={INVOICE_TAB_ID} label={T.INVOICE} />
          <Tab value={EMAIL_TAB_ID} label={T.EMAIL} />
          {isFortis && <Tab value={HISTORY_TAB_ID} label={T.HISTORY} />}
        </Tabs>

        <TabPanel index={INVOICE_TAB_ID} value={selectedTab}>
          {Array.isArray(allInvoices) && allInvoices.length > 0 ? (
            <>
              <ActivityHeader />
              <Divider sx={{ width: '100%' }} />
              <Grid container>
                {Children.toArray(
                  allInvoices.map((invoice, index) => (
                    <>
                      <InvoiceActivity invoice={invoice} showAppliedColumn align="inherit" marginY={2} />
                      {allInvoices.length - 1 !== index && <Divider sx={{ width: '100%' }} />}
                    </>
                  ))
                )}
              </Grid>
            </>
          ) : (
            <Box width="100%">
              <HHSectionPlaceholder title={T.NO_INVOICE_ACTIVITY_FOUND} />
            </Box>
          )}
        </TabPanel>
        <TabPanel index={EMAIL_TAB_ID} value={selectedTab}>
          <Grid rowGap={2} container mb={2}>
            {Array.isArray(emailEvents) && emailEvents.length > 0 ? (
              Children.toArray(
                emailEvents.map((paymentEmailEvent, index) => (
                  <>
                    <EmailActivity email={paymentEmailEvent} />
                    {emailEvents.length - 1 !== index && <Divider sx={{ width: '100%' }} />}
                  </>
                ))
              )
            ) : (
              <Box width="100%">
                <HHSectionPlaceholder title={T.NO_EMAIL_ACTIVITY_FOUND} />
              </Box>
            )}
          </Grid>
        </TabPanel>
        {isFortis && (
          <TabPanel index={HISTORY_TAB_ID} value={selectedTab}>
            {Array.isArray(paymentHistoryEvents) && paymentHistoryEvents.length > 0 ? (
              <>
                <ActivityHeader />
                <Divider sx={{ width: '100%' }} />
                <Grid container>
                  {Children.toArray(
                    historyWithTotals.map((historyEvent, index) => (
                      <>
                        <PaymentHistoryActivity historyEntry={historyEvent} align="inherit" marginY={2} />
                        {historyWithTotals.length - 1 !== index && <Divider sx={{ width: '100%' }} />}
                      </>
                    ))
                  )}
                </Grid>
              </>
            ) : (
              <Box width="100%">
                <HHSectionPlaceholder title={T.NO_PAYMENT_ACTIVITY_FOUND} />
              </Box>
            )}
          </TabPanel>
        )}
      </Box>
    </Component>
  )
}

PaymentActivityFeed.propTypes = {
  accountId: PropTypes.string,
  invoices: PropTypes.array,
  accountCredit: PropTypes.object,
  transactionStatus: PropTypes.string,
  emailEvents: PropTypes.array,
  paymentHistoryEvents: PropTypes.array,
  selectedTab: PropTypes.string.isRequired,
  onSelectedTabChange: PropTypes.func.isRequired,
  Component: PropTypes.element,
}

export default PaymentActivityFeed
